import { Affiliate, type AffiliateExportRow, type AffiliateRepository } from '@telespot/domain';
import { ParseBaseRepository } from '../../parse-base.repository';
import { AffiliateTopology, ParseAffiliateMapper } from './parse-affiliate.mapper';
import { OrganizationTopology } from '../organization';
import { UserTopology } from '../accounts';

export class ParseAffiliateRepository extends ParseBaseRepository implements AffiliateRepository {
  private readonly affiliateMapper = new ParseAffiliateMapper(this.parse);

  public async save(affiliate: Affiliate): Promise<string> {
    const parseAffiliate = this.affiliateMapper.fromDomain(affiliate);
    const { id } = await parseAffiliate.save(null, this.options);
    return id;
  }

  public async delete(affiliationID: string): Promise<void> {
    const parseAffiliate = this.subclasses.getSubclass(AffiliateTopology.TABLE).createWithoutData(affiliationID);
    await parseAffiliate.destroy(this.options);
  }

  public async countByOrganization(organizationId: string, searchTerm?: string): Promise<number> {
    const query = new this.parse.Query(AffiliateTopology.TABLE);
    const Organization: typeof this.parse.Object = this.parse.Object.extend(OrganizationTopology.TABLE);

    if (!!searchTerm && searchTerm.trim() !== '') {
      const userFilterQuery = new this.parse.Query(UserTopology.TABLE).matches(
        UserTopology.USERNAME,
        new RegExp(searchTerm, 'i')
      );

      query.matchesQuery(AffiliateTopology.USER, userFilterQuery);
    }

    query.equalTo(AffiliateTopology.ORGANIZATION, Organization.createWithoutData(organizationId));
    return query.count();
  }

  private static readonly COUNT_AFFILIATES_PAGINATION_LIMIT = 25;

  public async count(organizationId: string, searchTerm?: string): Promise<number> {
    const query = new this.parse.Query(AffiliateTopology.TABLE).equalTo(
      AffiliateTopology.ORGANIZATION,
      this.subclasses.getSubclass(OrganizationTopology.TABLE).createWithoutData(organizationId)
    );

    // Filter by username if searchTerm is provided
    if (!!searchTerm && searchTerm.trim() !== '') {
      const userFilterQuery = new this.parse.Query(UserTopology.TABLE).matches(
        UserTopology.USERNAME,
        new RegExp(searchTerm, 'i')
      );

      query.matchesQuery(AffiliateTopology.USER, userFilterQuery);
    }

    // Get the generator
    const generator = this.getTokenPaginationGenerator({
      limit: ParseAffiliateRepository.COUNT_AFFILIATES_PAGINATION_LIMIT,
      lastObjectId: undefined,
      query,
    });

    // Use the generator to count the number of affiliates
    let resultCount = 0;
    for await (const items of generator) {
      resultCount += items.length;
    }

    return resultCount;
  }

  public async isAffiliate(userId: string, organizationId: string): Promise<boolean> {
    const query = new this.parse.Query(AffiliateTopology.TABLE);
    query.equalTo(AffiliateTopology.USER, this.parse.User.createWithoutData(userId));
    query.equalTo(
      AffiliateTopology.ORGANIZATION,
      this.subclasses.getSubclass(OrganizationTopology.TABLE).createWithoutData(organizationId)
    );
    const result = await query.first(this.options);
    return result !== undefined;
  }

  public async findInOrganizationByName(organizationId: string, username: string): Promise<Affiliate> {
    const ParseOrganization = this.subclasses.getSubclass(OrganizationTopology.TABLE);

    const userQuery = new this.parse.Query(this.parse.User).equalTo(UserTopology.USERNAME, username);

    const affiliationQuery = new this.parse.Query(AffiliateTopology.TABLE)
      .matchesQuery(AffiliateTopology.USER, userQuery)
      .equalTo(AffiliateTopology.ORGANIZATION, ParseOrganization.createWithoutData(organizationId))
      .include(AffiliateTopology.USER);

    const affiliate = await affiliationQuery.first(this.options);

    return affiliate ? this.affiliateMapper.toDomain(affiliate) : undefined;
  }

  public async findInOrganization(organizationId: string, userId: string): Promise<Affiliate> {
    const ParseOrganization = this.subclasses.getSubclass(OrganizationTopology.TABLE);

    const affQuery = new this.parse.Query(AffiliateTopology.TABLE)
      .equalTo(AffiliateTopology.USER, this.parse.User.createWithoutData(userId))
      .equalTo(AffiliateTopology.ORGANIZATION, ParseOrganization.createWithoutData(organizationId))
      .include(AffiliateTopology.USER);

    const affiliate = await affQuery.first(this.options);

    return affiliate ? this.affiliateMapper.toDomain(affiliate) : undefined;
  }

  public async findInOrganizationByEmail(organizationId: string, email: string): Promise<Affiliate> {
    const organization = this.subclasses.getSubclass(OrganizationTopology.TABLE).createWithoutData(organizationId);

    const userQuery = new this.parse.Query(this.parse.User).equalTo(UserTopology.EMAIL, email.toLowerCase());

    const affiliationQuery = new this.parse.Query(AffiliateTopology.TABLE)
      .matchesQuery(AffiliateTopology.USER, userQuery)
      .equalTo(AffiliateTopology.ORGANIZATION, organization)
      .include(AffiliateTopology.USER);

    const affiliate = await affiliationQuery.first(this.options);

    return affiliate ? this.affiliateMapper.toDomain(affiliate) : undefined;
  }

  private static readonly EXPORT_AFFILIATES_PAGINATION_LIMIT = 50;

  public async exportAffiliates(organizationId: string): Promise<AffiliateExportRow[]> {
    const organization = this.subclasses.getSubclass(OrganizationTopology.TABLE).createWithoutData(organizationId);

    const query = new this.parse.Query(AffiliateTopology.TABLE)
      .equalTo(AffiliateTopology.ORGANIZATION, organization)
      .include(AffiliateTopology.USER)
      .include(AffiliateTopology.ORGANIZATION);

    const generator = this.getTokenPaginationGenerator({
      limit: ParseAffiliateRepository.EXPORT_AFFILIATES_PAGINATION_LIMIT,
      query,
      lastObjectId: undefined,
    });

    const rows: AffiliateExportRow[] = [];
    for await (const batch of generator) {
      for (const affiliate of batch) {
        const org = affiliate.get(AffiliateTopology.ORGANIZATION);
        const user = affiliate.get(AffiliateTopology.USER);

        rows.push({
          organizationId: org?.id ?? organizationId,
          organizationName: org?.get(OrganizationTopology.NAME) ?? '',
          userId: user?.id ?? '',
          username: user?.get(UserTopology.USERNAME) ?? '',
          email: user?.get(UserTopology.EMAIL) ?? '',
          emailVerified: user?.get(UserTopology.VERIFIED) ?? false,
        });
      }
    }

    return rows;
  }

  public async findUserIdsInOrganizations(organizationIds: string[]): Promise<string[]> {
    if (organizationIds.length === 0) return [];

    const ParseOrganization = this.subclasses.getSubclass(OrganizationTopology.TABLE);
    const organizationPointers = organizationIds.map((id) => ParseOrganization.createWithoutData(id));

    const query = new this.parse.Query(AffiliateTopology.TABLE)
      .containedIn(AffiliateTopology.ORGANIZATION, organizationPointers)
      .select(AffiliateTopology.USER);

    const generator = this.getTokenPaginationGenerator({
      limit: 100,
      query,
      lastObjectId: undefined,
    });

    const userIds = new Set<string>();
    for await (const batch of generator) {
      for (const affiliate of batch) {
        const id = affiliate.get(AffiliateTopology.USER)?.id;
        if (id) userIds.add(id);
      }
    }

    return [...userIds];
  }
}
